import type { IKoiSchema } from '@ksyos/koi/dist/koi';
import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from './schemaContainer';
import { replaceKoiError } from './replaceKoiError';

export type CompensationPayoutForServiceActivityUpdatableFields = {
    amount: number;
    endDate: string | null;
    serviceActivityId: string;
    startDate: string;
};

export type CompensationPayoutWithId = {
    compensationPayout: CompensationPayoutForServiceActivityUpdatableFields & CompensationPayoutEntityFields;
    id?: string;
};

export const compensationPayoutForServiceActivityUpdatableFields = {
    amount: Koi.number(),
    endDate: Koi.koi().endDate().allow(null),
    serviceActivityId: Koi.string().uuid(),
    startDate: Koi.koi().date(),
};

export type CompensationPayoutEntityFields = {
    serviceEmploymentId?: string | null;
    serviceGroupSettingId?: string | null;
    serviceId?: string | null;
    serviceOrganizationSettingId?: string | null;
};

export type CompensationPayoutForServiceActivity = CompensationPayoutForServiceActivityUpdatableFields &
    CompensationPayoutEntityFields & { id?: string };

export type CompensationPayoutForServiceActivityWithId = CompensationPayoutForServiceActivity & { id: string };

export const compensationPayoutForServiceActivitySchema = {
    ...compensationPayoutForServiceActivityUpdatableFields,
    serviceEmploymentId: Koi.string().uuid().allow(null).optional(),
    serviceGroupSettingId: Koi.string().uuid().allow(null).optional(),
    serviceId: Koi.string().uuid().allow(null).optional(),
    serviceOrganizationSettingId: Koi.string().uuid().allow(null).optional(),
};

export const compensationPayoutForServiceActivityUpdatableFieldsSchema: ISchemaContainer<
    CompensationPayoutForServiceActivityUpdatableFields[]
> = {
    schema: Koi.object({ ...compensationPayoutForServiceActivityUpdatableFields }).options({
        convert: false,
        presence: 'required',
    }),
};

export function compensationPayoutUpdateSchema(
    minDate: string | Date,
    maxDate?: string | Date | null
): ISchemaContainer<CompensationPayoutForServiceActivityUpdatableFields> {
    const endDate = Koi.koi().date().endDate().allow(null);
    const startDate = Koi.koi().date();

    const dateValidation = (date: IKoiSchema) => {
        const maxDateValidation = maxDate
            ? date.dateSameOrAfter(minDate).dateSameOrBefore(maxDate)
            : date.dateSameOrAfter(minDate);

        return maxDateValidation.error((errors) => {
            const updatedErrors = replaceKoiError(errors, 'koi.dateSameOrAfter', 'date.beforeStartServiceRight');

            return replaceKoiError(updatedErrors, 'koi.dateSameOrBefore', 'date.afterEndServiceRight');
        });
    };

    return {
        schema: Koi.object({
            amount: Koi.number(),
            endDate: dateValidation(endDate),
            serviceActivityId: Koi.string().uuid(),
            startDate: dateValidation(startDate),
        }),
    };
}

export const compensationPayoutCreateValidationSchema = Koi.object({ ...compensationPayoutForServiceActivitySchema });

export const compensationPayoutEditValidationSchema = Koi.object({
    ...compensationPayoutForServiceActivityUpdatableFields,
});

export const compensationPayoutWithIdSchema = Koi.object({
    compensationPayout: compensationPayoutForServiceActivitySchema,
    id: Koi.string().uuid().optional(),
});
